import React, { useMemo } from 'react';
import classNames from 'classnames';
import styles from '@src/emp-3.0/components/MappedEventBuilder/MappingRulesBuilder/index.less';
import { AutoComplete as AutoCompleteStyled } from '../styled';
import { Button } from 'antd';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import calcShortcutInterval, {
  calcShortcutIntervalWithinBoundary,
} from '@utils/calcShortcutInterval';
import { DATE_SHORTCUTS } from '@utils/constants';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import { mappedEventDimensionType } from '@src/common/constant/enum';
import { useGetBlockEventsName, useGetMappedStatusEvent } from '@api/emp/queryHooks';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { getEmptyRuleGroupInAPIForm } from '@src/emp-3.0/utils';
import { EStringOp, ESystemType, IEventRule, IEventRuleItem, Operator } from '@src/emp-3.0/types';
import RuleItem from '@src/emp-3.0/components/MappedEventBuilder/MappingRulesBuilder/RuleItem';
import MetadataBox from '../MetadataBox';

const getEmptyRuleItem = () => {
  return getEmptyRuleGroupInAPIForm().mapping_rules[0][0];
};

interface IProps {
  event: IEventRule;
  onDelete?: (event: IEventRule) => void;
  onClick?: (event: IEventRule) => void;
  onChange?: (event: IEventRule) => void;
  index: number;
  isActive?: boolean;
  isHighlight?: boolean;
  errorFields?: string[];
  errorIndexs?: number[];
  src?: string;
}

const EventBox: React.FC<IProps> = ({
  event,
  index,
  isHighlight,
  onDelete,
  onChange,
  onClick,
  errorFields,
  errorIndexs,
  ...rest
}) => {
  const [from, to] = calcShortcutIntervalWithinBoundary(DATE_SHORTCUTS.last7Days, ESystemType.EMP);
  const { data: eventNameData, isFetching: isGetEventNameLoading } = useQueryDimensionData(
    {
      fields: [mappedEventDimensionType.all_event_name],
      interval: `${from.format()}/${to.format()}`,
      filters: [],
    },
    500,
  );
  const { data: mappedEventNameData } = useGetMappedStatusEvent('event_name');
  const { data: blockedEventsName } = useGetBlockEventsName();

  const loadingSuffix = useMemo(() => {
    if (isGetEventNameLoading) return <LoadingOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />;
    return <DownOutlined />;
  }, [isGetEventNameLoading]);

  const rawEventNameOptions = useMemo(() => {
    return ((eventNameData as any[]) ?? []).map((item) => ({
      label: item[mappedEventDimensionType.all_event_name],
      value: item[mappedEventDimensionType.all_event_name],
    }));
  }, [eventNameData]);

  const addRuleItem = (type: 'OR' | 'AND') => {
    const newEvent = {
      ...event,
      mapping_rules: [...event.mapping_rules.map((group) => [...group])],
    };
    if (type === 'OR') {
      const lastOrGroup = newEvent.mapping_rules[newEvent.mapping_rules.length - 1];
      lastOrGroup.push(getEmptyRuleItem());
    } else {
      newEvent.mapping_rules.push([getEmptyRuleItem()]);
    }
    onChange?.(newEvent);
  };

  const deleteAndLine = (ruleAndIndex) => {
    // change and rule to or rule
    const curRule = event.mapping_rules[ruleAndIndex];
    event.mapping_rules[ruleAndIndex - 1].push(...curRule);
    event.mapping_rules.splice(ruleAndIndex, 1);
    onChange?.(event);
  };

  const deleteRuleItem = (ruleAndIndex, ruleOrIndex) => {
    event.mapping_rules[ruleAndIndex].splice(ruleOrIndex, 1);

    if (event.mapping_rules[ruleAndIndex].length === 0) {
      // Delete last orRule in andGroup need to delete this andRule as well
      event.mapping_rules.splice(ruleAndIndex, 1);
    }

    if (event.mapping_rules.length === 0) {
      // Delete last rule, clear rule input box.
      const curRule = getEmptyRuleItem();
      event.mapping_rules[0] = [curRule];
    }
    onChange?.(event);
  };
  const addMetadataItem = () => {
    const item = [
      {
        key: '',
        value: [],
        op: EStringOp.EQ as Operator,
      },
    ];
    if (!event.metadata || event.metadata.length === 0) {
      event.metadata = [item];
    } else {
      event.metadata.push(item);
    }
    onChange?.(event);
  };

  const onDeleteMetadataItem = (eventIndex: number, metadataIndex: number) => {
    const newEvent = { ...event };
    newEvent.metadata.splice(metadataIndex, 1);
    onChange?.(newEvent);
  };
  const onChangeMetadataItem = (metadataIndex: number, value: any) => {
    const newEvent = { ...event };
    if (!newEvent.metadata || newEvent.metadata.length === 0) {
      newEvent.metadata = [];
    }
    newEvent.metadata[metadataIndex] = value;
    onChange?.(newEvent);
  };

  const renderEventRules = (
    rawEventName: string[],
    rules: IEventRuleItem[],
    eventIndex: number,
    ruleAndIndex: number,
  ) => {
    return (
      <div key={`key_${eventIndex}_${ruleAndIndex}`}>
        <div className={styles.andRuleContent}>
          {ruleAndIndex !== 0 && (
            <div className={styles.andRuleLine}>
              <div className={styles.lineLeft}></div>
              <div className={styles.andText}>AND</div>
              <div className={styles.lineRight}></div>
              <span className={styles.deleteIcon}>
                <Button
                  type="text"
                  icon={<DeleteSvg />}
                  onClick={() => deleteAndLine(ruleAndIndex)}
                />
              </span>
            </div>
          )}
          {rules.map((rule, ruleOrIndex) => (
            <RuleItem
              errorFields={errorFields || []}
              rawEventName={rawEventName?.[0] || ''}
              showHeader={ruleOrIndex !== 0}
              key={`key_${eventIndex}_${ruleAndIndex}_${ruleOrIndex}`}
              rule={rule}
              onChange={(newRule) => {
                rule = newRule;
                onChange?.(event);
              }}
              onDelete={() => {
                deleteRuleItem(ruleAndIndex, ruleOrIndex);
              }}
              mappingRules={event?.mapping_rules}
              {...rest}
            />
          ))}
        </div>
      </div>
    );
  };

  // To ensure the UI structure, generate the default data structures.
  if (Array.isArray(event.mapping_rules) && event.mapping_rules?.length === 0) {
    event.mapping_rules.push([getEmptyRuleItem()]);
  }

  const rawEventName = useMemo(() => {
    return event.value?.[0];
  }, [event.value]);

  return (
    <div
      className={classNames(styles.rawEvent, isHighlight ? styles.active : null)}
      onClick={() => onClick?.(event)}
    >
      <div className={styles.rawEventTitle}>
        {index !== 0 && <span className={styles.orTag}>OR</span>}
        <span style={{ display: 'flex', alignItems: 'center', marginRight: 0, flex: 1 }}>
          <span>Raw Event = </span>
          <AutoCompleteStyled
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ marginLeft: 15, flex: 1 }}
            data-test-id={`raw_event_name_${index + 1}`}
            suffixIcon={loadingSuffix}
            disabled={event.disable}
            value={rawEventName}
            status={errorFields?.includes('raw_event_name') && errorIndexs ? 'error' : undefined}
            options={rawEventNameOptions}
            mappedData={mappedEventNameData?.data}
            blockedData={blockedEventsName}
            placeholder="Type or select..."
            filterOption={true}
            onChange={(value) => {
              if (value === event.value?.[0]) return; // Avoid unnecessary updates
              // Some time the raw event name is '', should BE to check.
              const newEvent = {
                ...event,
                value: value ? [value] : [],
                metadata: value ? event.metadata : [],
              };
              onChange?.(newEvent);
            }}
          />
        </span>
        <span className={styles.deleteIcon}>
          <Button
            type="text"
            icon={<DeleteSvg />}
            disabled={event.disable} // disable delete button when this event is disabled
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(event);
            }}
          />
        </span>
      </div>
      {
        <div className={styles.rawEventContent}>
          {event.mapping_rules?.map((rules, ruleAndIndex) => {
            return renderEventRules(event.value, rules, index, ruleAndIndex);
          })}
        </div>
      }
      <div className={styles.rawEventFooter}>
        <span onClick={() => addRuleItem('OR')}>Add OR Rule</span>
        <span onClick={() => addRuleItem('AND')}>Add AND Rule</span>
      </div>
      {(event?.metadata ?? []).map((item, subAndIndex) => {
        return (
          <MetadataBox
            errorFields={errorFields}
            key={`metadataGroup_${subAndIndex}`}
            index={subAndIndex}
            data={item}
            onDelete={() => {
              onDeleteMetadataItem(index, subAndIndex);
            }}
            onChange={(subOrIndex, value) => {
              onChangeMetadataItem(subAndIndex, value);
            }}
          />
        );
      })}
      {rawEventName && !event?.disableMetaDataAddition && (
        <div className={styles.metadataFooter}>
          <span onClick={() => addMetadataItem()}>Add Metadata Rule</span>
        </div>
      )}
    </div>
  );
};

export default EventBox;
